/**
 * DatabaseDiagnostic Component
 * Diagnostic tool to list all database tables
 */

import { useState, useEffect } from 'react';
import { Database, CheckCircle2, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface TableInfo {
  status: string;
  method?: string;
  tables?: string[];
  missingTables?: string[];
  count?: number;
  totalChecked?: number;
  note?: string;
  error?: string;
}

export function DatabaseDiagnostic() {
  const [loading, setLoading] = useState(false);
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchTables = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-36aa765f/database/tables`,
        {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
          },
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch tables');
      }
      
      setTableInfo(data);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const configurationTables = ['banks', 'locations', 'projects', 'operation_nature', 'tags', 'bank_accounts', 'entry_tags'];
  
  const existingConfigTables = tableInfo?.tables?.filter(t => configurationTables.includes(t)) || [];
  const missingConfigTables = configurationTables.filter(t => !tableInfo?.tables?.includes(t));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            <CardTitle>Diagnóstico do Banco de Dados</CardTitle>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTables}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Atualizar</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-2">
            <XCircle className="h-5 w-5 text-danger-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-danger-900">Erro ao buscar tabelas</p>
              <p className="text-sm text-danger-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {loading && !tableInfo && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        )}

        {tableInfo && (
          <>
            {/* Summary */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="p-3 bg-neutral-50 rounded-lg">
                <p className="text-sm text-neutral-600">Total de Tabelas</p>
                <p className="text-2xl font-semibold text-neutral-900">
                  {tableInfo.count || tableInfo.tables?.length || 0}
                </p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-lg">
                <p className="text-sm text-neutral-600">Método de Detecção</p>
                <p className="text-sm font-medium text-neutral-900 capitalize">
                  {tableInfo.method === 'direct' ? 'SQL Direto' : 'Inferência'}
                </p>
              </div>
            </div>

            {/* Configuration Tables Status */}
            <div className="space-y-2">
              <h3 className="font-medium text-neutral-900">Status das Tabelas de Configurações</h3>
              <div className="grid gap-2">
                {existingConfigTables.length > 0 && (
                  <div className="p-3 bg-success-50 border border-success-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-4 w-4 text-success-600" />
                      <p className="font-medium text-success-900">
                        Tabelas Criadas ({existingConfigTables.length}/{configurationTables.length})
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {existingConfigTables.map(table => (
                        <Badge key={table} variant="outline" className="bg-success-100 text-success-700 border-success-300">
                          {table}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {missingConfigTables.length > 0 && (
                  <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="h-4 w-4 text-warning-600" />
                      <p className="font-medium text-warning-900">
                        Tabelas Faltando ({missingConfigTables.length}/{configurationTables.length})
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {missingConfigTables.map(table => (
                        <Badge key={table} variant="outline" className="bg-warning-100 text-warning-700 border-warning-300">
                          {table}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm text-warning-700 mt-2">
                      Execute a migration <code className="px-1 py-0.5 bg-warning-100 rounded">/supabase/migrations/20241101_add_new_configurations.sql</code> no SQL Editor do Supabase.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* All Tables List */}
            {tableInfo.tables && tableInfo.tables.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-neutral-900">Todas as Tabelas ({tableInfo.tables.length})</h3>
                <div className="max-h-60 overflow-y-auto border border-neutral-200 rounded-lg p-3 bg-neutral-50">
                  <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                    {tableInfo.tables.map((table) => (
                      <div
                        key={table}
                        className="text-sm text-neutral-700 font-mono px-2 py-1 bg-white rounded border border-neutral-200"
                      >
                        {table}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tableInfo.note && (
              <p className="text-sm text-neutral-600 italic">{tableInfo.note}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
